import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "@styles/CommonEditorPageLayout.module.css";
import TimelineForm, { TimelineDataWithError } from "@components/forms/TimelineForm/TimelineForm";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import { fetchMemorial, updateMemorialTimeline } from "@services/memorialService";
import { TimelineEntryAdd, TimelineEntryChange, TimelineUpdate } from "@models/Timeline";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { AxiosProgressEvent } from "axios";
import { useAuth } from "@context/auth-context";
import { getRootDomain } from "@util/domain";
import { Memorial } from "@models/Memorial";

const TimelineEditorPage = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const { authState } = useAuth();
    const { user } = authState;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [timelineChanges, setTimelineChanges] = useState<TimelineUpdate>({
        added: [], updated: [], deleted: [],
    });
    const [timeline, setTimeline] = useState<TimelineDataWithError[]>([]);
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");
    const [iframeKey, setIframeKey] = useState(Date.now());
    const [qrKey, setQrKey] = useState("");
    const [memorial, setMemorial] = useState<Memorial | null>(null);

    const hasUnsavedChanges = (timelineChanges.added.length > 0)
        || (timelineChanges.updated.length > 0) || (timelineChanges.deleted.length > 0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchMemorial(id);
                setMemorial(data);
                setTimeline(data.timeline.map((elem) => ({
                    ...elem,
                    media: elem.media?.src,
                    hasError: false,
                    mediaError: "",
                })));
                setQrKey(data.qrKey);
            } catch (err) {
                setError("Failed to fetch memorial information.");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [id]);

    const validateTimeline = (val: TimelineDataWithError[]) => {
        let err = "";
        val.forEach((entry) => {
            if (entry.hasError || entry.mediaError) {
                err = "Timeline error.";
            }
        });
        return err;
    };

    const onTimelineChange = useCallback((updatedEntries: TimelineDataWithError[]) => {
        setTimeline(updatedEntries);
    }, []);

    const onTimelineAdd = useCallback((newEntry: TimelineEntryAdd) => {
        setTimelineChanges((prev) => ({
            ...prev,
            added: [...prev.added, newEntry],
        }));
    }, []);

    const onTimelineUpdate = useCallback((updatedEntry: TimelineEntryChange) => {
        setTimelineChanges((prev) => {
            const isInAdded = prev.added.some((entry) => entry.id === updatedEntry.id);

            if (isInAdded) {
                // Update the entry in added
                return {
                    ...prev,
                    added: prev.added.map((entry) => (
                        entry.id === updatedEntry.id ? { ...entry, ...updatedEntry } : entry)),
                };
            }
            // Add or update in updated
            return {
                ...prev,
                updated: [
                    ...prev.updated.filter((entry) => entry.id !== updatedEntry.id), updatedEntry],
            };
        });
    }, []);

    const onTimelineDelete = useCallback((objId: string) => {
        setTimelineChanges((prev) => {
            const isInAdded = prev.added.some((entry) => entry.id === objId);
            const isInUpdated = prev.updated.some((entry) => entry.id === objId);

            return {
                // Remove from other fields if exists
                added: prev.added.filter((entry) => entry.id !== objId),
                updated: prev.updated.filter((entry) => entry.id !== objId),
                // Only add to deleted if it's not in added or updated
                deleted: isInAdded || isInUpdated ? prev.deleted : [...prev.deleted, objId],
            };
        });
    }, []);

    const progressCallback = useCallback((progressEvent: AxiosProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
    }, []);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        const timelineValidationError = validateTimeline(timeline);
        if (timelineValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                if (!id) {
                    throw new Error("No ID.");
                }
                if (hasUnsavedChanges) {
                    const updatedTimeline = await updateMemorialTimeline(
                        id,
                        timelineChanges,
                        progressCallback,
                    );
                    setTimeline(updatedTimeline.map((elem) => ({
                        ...elem,
                        media: elem.media?.src,
                        hasError: false,
                        mediaError: "",
                    })));
                }
                setTimelineChanges({
                    added: [], updated: [], deleted: [],
                });
                setIframeKey(Date.now()); // Update iframe key to refresh preview
            } catch (err) {
                const msg = "Failed to save memorial information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const baseMemorialUrl = memorial?.iframeUrl ? `${memorial.iframeUrl}?memorialId=${memorial.id}` : `${getRootDomain(memorial?.subdomain)}/memorials/${user?.userType === "demo" ? "example" : (memorial?.urlSlug || memorial?.id)}`;

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={uploadProgress}
                    showVideoMessage
                    error={error}
                />
            );
        }

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>Life Timeline Editor</h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.editorLayout}>
                    <div className={styles.formSection}>
                        <div className={styles.contentContainer}>
                            <p className={styles.freeVersionNoteMobile}>
                                View this page on a desktop or laptop to see a live preview of your memorial alongside the editor.
                            </p>
                            <p className={styles.note}>Timeline will be automatically ordered chronologically on save.</p>
                            <TimelineForm
                                state={timeline}
                                onChange={onTimelineChange}
                                onAdd={onTimelineAdd}
                                onUpdate={onTimelineUpdate}
                                onDelete={onTimelineDelete}
                            />
                        </div>
                    </div>

                    <div className={styles.previewSection}>
                        <h2>Preview</h2>
                        <div className={styles.previewContainer}>
                            <iframe
                                key={iframeKey}
                                title="Preview Frame"
                                src={`${baseMemorialUrl}#timeline`}
                                className={styles.previewFrame}
                            />
                            {hasUnsavedChanges && (
                                <div className={styles.previewOverlay}>
                                    <p>Save changes to refresh the preview</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {submitErrorMessage && (
                    <p className={styles.errorMessage}>{submitErrorMessage}</p>
                )}
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default TimelineEditorPage;
